import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  catchError,
  concat,
  EMPTY,
  expand,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  CommitResponse,
  CreateUploadSessionRequest,
  ExpedienteUploadEvent,
  UploadSessionResponse,
} from '../../../../domain/src/lib/ingest/expediente-upload.model';
import {
  expedienteBaseFromIso,
  groupByRole,
  parseHashHex,
  REQUIRED_EXPEDIENTE_ROLES,
} from './expediente-detect';

/**
 * Cliente de carga RESUMABLE de expedientes ISO contra /api/uploads.
 *
 * Protocolo (el interceptor api.vault antepone environment.API_VAULT + auth):
 *   POST   /api/uploads                         -> crea sesión
 *   HEAD   /api/uploads/{sid}/files/{name}       -> Upload-Offset (reanudar)
 *   PATCH  /api/uploads/{sid}/files/{name}       -> appendea un chunk
 *   POST   /api/uploads/{sid}/commit             -> verifica fixity + publica
 */
@Injectable({ providedIn: 'root' })
export class ExpedienteUploadService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);

  private readonly path = '/api/uploads';
  private static readonly CHUNK = 8 * 1024 * 1024; // 8 MB

  private tenantHeaders(): HttpHeaders {
    const ctx = this.serverContext.getContext();
    let headers = new HttpHeaders();
    if (ctx.tenantId) headers = headers.set('X-Tenant-ID', ctx.tenantId);
    return headers;
  }

  // ------------------------------------------------------- primitivas
  private createSession$(req: CreateUploadSessionRequest): Observable<UploadSessionResponse> {
    return this.http.post<UploadSessionResponse>(this.path, req, {
      headers: this.tenantHeaders(),
    });
  }

  private commit$(sessionId: string): Observable<CommitResponse> {
    return this.http.post<CommitResponse>(`${this.path}/${sessionId}/commit`, {}, {
      headers: this.tenantHeaders(),
    });
  }

  private getOffset$(sessionId: string, filename: string): Observable<number> {
    const url = `${this.path}/${sessionId}/files/${encodeURIComponent(filename)}`;
    return this.http
      .head(url, { headers: this.tenantHeaders(), observe: 'response' })
      .pipe(map((resp) => parseInt(resp.headers.get('Upload-Offset') ?? '0', 10)));
  }

  /** Appendea un chunk desde `offset`. Devuelve el nuevo offset del servidor. */
  private patchChunk$(sessionId: string, file: File, offset: number): Observable<number> {
    const end = Math.min(offset + ExpedienteUploadService.CHUNK, file.size);
    const chunk = file.slice(offset, end);
    const url = `${this.path}/${sessionId}/files/${encodeURIComponent(file.name)}`;
    const headers = this.tenantHeaders()
      .set('Upload-Offset', String(offset))
      .set('Content-Type', 'application/offset+octet-stream');

    return this.http.patch(url, chunk, { headers, observe: 'response' }).pipe(
      map((resp) => parseInt(resp.headers.get('Upload-Offset') ?? String(end), 10)),
      catchError((err: HttpErrorResponse) => {
        // 409: el servidor tiene otro offset -> reanudar desde ahí
        if (err.status === 409) {
          return of(parseInt(err.headers.get('Upload-Offset') ?? String(offset), 10));
        }
        return throwError(() => err);
      }),
    );
  }

  /** Sube un archivo completo por chunks (resumable). Emite bytes enviados acumulados. */
  private uploadFile$(sessionId: string, file: File): Observable<number> {
    return this.getOffset$(sessionId, file.name).pipe(
      switchMap((start) =>
        of(start).pipe(
          expand((offset) =>
            offset >= file.size ? EMPTY : this.patchChunk$(sessionId, file, offset),
          ),
        ),
      ),
    );
  }

  // ------------------------------------------------------- orquestación
  /**
   * Sube un expediente completo (1 .iso + 5 sidecars): valida formato, lee los
   * hashes declarados de los sidecars, crea la sesión, sube cada archivo de
   * forma resumable y hace commit. Emite progreso y, al final, el commit.
   */
  uploadExpediente$(params: {
    files: readonly File[];
    projectName: string;
    companyName?: string | null;
    subfolderPath?: string | null;
    description?: string | null;
  }): Observable<ExpedienteUploadEvent> {
    const byRole = groupByRole(params.files);
    const iso = byRole.get('iso');
    if (!iso) {
      return throwError(() => new Error('El expediente no contiene un archivo .iso'));
    }
    const missing = REQUIRED_EXPEDIENTE_ROLES.filter((r) => !byRole.has(r));
    if (missing.length) {
      return throwError(
        () => new Error(`Expediente incompleto: faltan sidecars ${missing.join(', ')}`),
      );
    }

    const ordered: File[] = [
      iso,
      ...REQUIRED_EXPEDIENTE_ROLES.filter((r) => r !== 'iso').map((r) => byRole.get(r)!),
    ];
    const overallTotal = ordered.reduce((acc, f) => acc + f.size, 0);
    const prefix: number[] = [];
    let acc = 0;
    for (const f of ordered) {
      prefix.push(acc);
      acc += f.size;
    }

    const md5File = byRole.get('md5')!;
    const sha1File = byRole.get('sha1')!;

    return from(Promise.all([md5File.text(), sha1File.text()])).pipe(
      switchMap(([md5Text, sha1Text]) => {
        const req: CreateUploadSessionRequest = {
          project_name: params.projectName,
          company_name: params.companyName ?? null,
          subfolder_path: params.subfolderPath ?? null,
          description: params.description ?? '',
          expediente_base: expedienteBaseFromIso(iso.name),
          files: ordered.map((f) => ({ filename: f.name, size: f.size })),
          declared_md5: parseHashHex(md5Text),
          declared_sha1: parseHashHex(sha1Text),
        };

        return this.createSession$(req).pipe(
          switchMap((session) => {
            const sid = session.session_id;
            const fileStreams: Observable<ExpedienteUploadEvent>[] = ordered.map((f, i) =>
              this.uploadFile$(sid, f).pipe(
                map(
                  (sent): ExpedienteUploadEvent => ({
                    type: 'progress',
                    filename: f.name,
                    overallSent: prefix[i] + sent,
                    overallTotal,
                  }),
                ),
              ),
            );
            const commit$ = this.commit$(sid).pipe(
              map((result): ExpedienteUploadEvent => ({ type: 'committed', result })),
            );
            return concat(...fileStreams, commit$);
          }),
        );
      }),
    );
  }
}
